import React, { useState, useId } from "react";
import { IListInfoObj } from "../../types/genericTypes";
import "./Tag.scss";
import { getNriAccountType } from "../../utils/entitySubType";

export interface ITagProps {
  defaultValue?: number | string; // default selected value
  tagList: Array<IListInfoObj>; // list of all tags
  className?: string; // extra css class if needed
  onSelect: (id: string | number) => void; // the function to be called onSelect
  bool?: { lhs: any; rhs: any; className: string }; //rhs will be checked with tag value
  disabled?: boolean;
  disabledClassName?: string;
  id?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  required?: boolean;
  ariaInvalid?: boolean;
  isNriFlow?: any;
}

const Tag: React.FC<ITagProps> = (props: ITagProps) => {
  const [selectedValue, setSelectedValue] = useState(props.defaultValue);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const generatedId = useId();

  React.useEffect(() => {
    setSelectedValue(props.defaultValue);
  }, [props.defaultValue]);

  function tagSelected(value: string | number) {
    if (props.disabled) return;
    setSelectedValue(value);
    return props.onSelect(value);
  }

  function handleKeyDown(e: React.KeyboardEvent, index: number, value: string | number) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      tagSelected(value);
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(index + 1, props.tagList.length - 1);
      setFocusedIndex(next);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const prev = Math.max(index - 1, 0);
      setFocusedIndex(prev);
    }
  }

  const listRef = React.useRef<HTMLUListElement>(null);
  React.useEffect(() => {
    const items = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    items?.[focusedIndex]?.focus();
  }, [focusedIndex]);

  return (
    <ul
      id={props.id}
      ref={listRef}
      className={
        "tag_navigation_content " + (props.className ? props.className : "")
      }
      role="listbox"
      aria-label={
        props.ariaLabelledBy
          ? undefined
          : props.ariaLabel || "Options"
      }
      aria-labelledby={props.ariaLabelledBy}
      aria-describedby={props.ariaDescribedBy}
      aria-required={props.required ? true : undefined}
      aria-invalid={props.ariaInvalid ? true : undefined}
      aria-orientation="horizontal"
    >
      {props.tagList.map((tag, index) => {
        const optionId = `${props.id || generatedId}-opt-${index}`;
        const isSelected =
          tag.value.toString() ===
          (selectedValue && selectedValue.toString());
        return (
          <li
            id={optionId}
            className={
              "tag" +
              (isSelected ? " active" : "") +
              (props.bool && props.bool.lhs && props.bool.rhs === tag.value
                ? props.bool.className
                : "") +
              (props?.disabled !== null &&
                props?.disabled !== undefined &&
                props?.disabled
                ? props?.disabledClassName
                : "")
            }
            key={tag.value}
            role="option"
            aria-selected={!!isSelected}
            tabIndex={props.disabled ? -1 : index === focusedIndex ? 0 : -1}
            onClick={() => { setFocusedIndex(index); tagSelected(tag.value); }}
            onKeyDown={(e) => handleKeyDown(e, index, tag.value)}
          >
            {getNriAccountType(props?.isNriFlow)} {tag.display}
          </li>
        );
      })}
    </ul>
  );
};

export interface IEsignTagProps extends ITagProps {
  isInfo?: boolean;
}

export const EsignTag: React.FC<IEsignTagProps> = (props) => {
  const [selectedValue, setSelectedValue] = useState(props.defaultValue);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const listRef = React.useRef<HTMLUListElement>(null);
  const generatedId = useId();

  React.useEffect(() => {
    setSelectedValue(props.defaultValue);
  }, [props.defaultValue]);

  React.useEffect(() => {
    const items = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    items?.[focusedIndex]?.focus();
  }, [focusedIndex]);

  function tagSelected(value: string | number) {
    setSelectedValue(value);
    return props.onSelect(value);
  }

  function handleKeyDown(e: React.KeyboardEvent, index: number, value: string | number) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      tagSelected(value);
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex(Math.min(index + 1, props.tagList.length - 1));
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex(Math.max(index - 1, 0));
    }
  }

  return (
    <ul ref={listRef} className="esignTagList" role="listbox" aria-label={props.ariaLabel || "E-sign options"} aria-invalid={props.ariaInvalid ? true : undefined}>
      {props.tagList.map((tag, index) => {
        const optionId = `${props.id || generatedId}-esign-opt-${index}`;
        const isSelected = tag.value === selectedValue;
        return (
          <li
            id={optionId}
            className={
              "esignTag" +
              (isSelected ? " activeEsignTag" : "")
            }
            key={tag.value}
            role="option"
            aria-selected={!!isSelected}
            tabIndex={index === focusedIndex ? 0 : -1}
            onClick={() => { setFocusedIndex(index); tagSelected(tag.value); }}
            onKeyDown={(e) => handleKeyDown(e, index, tag.value)}
          >
            {props.isInfo && <span className="tagInfo">{tag.info}</span>}
            <span className="tagDisplay">{tag.display}</span>
          </li>
        );
      })}
    </ul>
  );
};

export default Tag;
